/** Secção de documentação custom (smiley + subtítulo + título + descrição). */

export const POWER_DOC_SECTION_POSITIONS = [
  { id: 'after-overview', label: 'Após «Sobre este power»' },
  {
    id: 'after-special',
    label: 'Após secções especiais (AllPowers / EveryPower / Coleção)',
  },
  { id: 'after-pstyle', label: 'Após Pstyle' },
  { id: 'after-avatar-frames', label: 'Após Avatar frames' },
  { id: 'after-smilies', label: 'Após Smilies e Peões' },
  { id: 'before-tutorial', label: 'Antes do Tutorial' },
  { id: 'after-tutorial', label: 'Após o Tutorial' },
  { id: 'before-related', label: 'Antes de «Explore mais»' },
] as const;

export type PowerDocSectionPosition = (typeof POWER_DOC_SECTION_POSITIONS)[number]['id'];

export type PowerDocSection = {
  id: string;
  /** Código do smiley (ex.: read, smile) — sem parênteses. */
  smiley: string;
  subtitle: string;
  title: string;
  /** HTML rich text. */
  description: string;
  position: PowerDocSectionPosition;
};

const POSITION_IDS = new Set<string>(POWER_DOC_SECTION_POSITIONS.map((p) => p.id));

export function isPowerDocSectionPosition(v: unknown): v is PowerDocSectionPosition {
  return typeof v === 'string' && POSITION_IDS.has(v);
}

function newSectionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `doc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyPowerDocSection(
  overrides?: Partial<Omit<PowerDocSection, 'id'>> & { id?: string }
): PowerDocSection {
  return {
    id: overrides?.id ?? newSectionId(),
    smiley: overrides?.smiley ?? 'read',
    subtitle: overrides?.subtitle ?? 'Documentação',
    title: overrides?.title ?? '',
    description: overrides?.description ?? '',
    position: overrides?.position ?? 'after-overview',
  };
}

export function normalizePowerDocSection(raw: unknown): PowerDocSection | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const smiley = typeof o.smiley === 'string' ? o.smiley.trim() : '';
  const subtitle = typeof o.subtitle === 'string' ? o.subtitle : '';
  const title = typeof o.title === 'string' ? o.title : '';
  const description = typeof o.description === 'string' ? o.description : '';
  const position = isPowerDocSectionPosition(o.position) ? o.position : 'after-overview';
  const id = typeof o.id === 'string' && o.id.trim() ? o.id.trim() : newSectionId();
  return {
    id,
    smiley: smiley || 'read',
    subtitle,
    title,
    description,
    position,
  };
}

export function normalizePowerDocSections(raw: unknown): PowerDocSection[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizePowerDocSection).filter((s): s is PowerDocSection => Boolean(s));
}

export function powerDocSectionsForPersist(sections: PowerDocSection[]): PowerDocSection[] {
  return sections
    .map((s) => ({
      id: s.id,
      smiley: s.smiley.trim() || 'read',
      subtitle: s.subtitle.trim(),
      title: s.title.trim(),
      description: s.description.trim(),
      position: isPowerDocSectionPosition(s.position) ? s.position : 'after-overview',
    }))
    .filter((s) => s.title || s.subtitle || s.description);
}

export type PowerDocSectionsSource = {
  docSectionsEnabled?: boolean;
  docSections?: PowerDocSection[];
};

/** Secções activas num slot da página pública. */
export function getPowerDocSectionsAt(
  power: PowerDocSectionsSource,
  position: PowerDocSectionPosition,
  options?: { excludeIds?: string[] }
): PowerDocSection[] {
  if (!power.docSectionsEnabled) return [];
  const exclude = options?.excludeIds?.length ? new Set(options.excludeIds) : null;
  return (power.docSections ?? []).filter((s) => {
    if (s.position !== position) return false;
    if (exclude?.has(s.id)) return false;
    return true;
  });
}
